import React, { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, ArrowRight, Home as HomeIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const { site } = useSite();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user && user.role) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center p-4 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            "linear-gradient(45deg, transparent 48%, #DC2626 49%, #DC2626 51%, transparent 52%)",
          backgroundSize: "40px 40px",
        }}
        aria-hidden
      />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          {site?.logo_url && (
            <img
              src={site.logo_url}
              alt={site?.business_name}
              className="h-24 w-auto mx-auto bg-white p-3"
            />
          )}
          <h1 className="mt-6 font-heading text-2xl font-bold text-white uppercase tracking-tight">
            Admin Access
          </h1>
          <p className="text-neutral-400 text-sm mt-2">
            Sign in to manage your website content
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white p-8 border-4 border-[#DC2626]"
          style={{ boxShadow: "10px 10px 0 0 rgba(220,38,38,0.3)" }}
        >
          <div className="space-y-5">
            <div>
              <label className="text-xs font-heading uppercase tracking-widest text-neutral-700 mb-2 block">
                Email
              </label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  data-testid="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-neutral-200 focus:border-[#DC2626] focus:outline-none"
                  placeholder="admin@jscomplete.com"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-heading uppercase tracking-widest text-neutral-700 mb-2 block">
                Password
              </label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  data-testid="login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-neutral-200 focus:border-[#DC2626] focus:outline-none"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <button
              data-testid="login-submit"
              type="submit"
              disabled={submitting}
              className="btn-primary-cta w-full justify-center disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign In"}
              <ArrowRight size={18} strokeWidth={2.5} />
            </button>
          </div>
        </form>

        <div className="mt-6 text-center">
          <Link
            data-testid="back-to-site"
            to="/"
            className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm font-heading uppercase tracking-widest"
          >
            <HomeIcon size={14} />
            Back to site
          </Link>
        </div>
      </div>
    </div>
  );
}
